"use client";

import Link from "next/link";
import { useState } from "react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { PriceList } from "@/app/_components/price-list";

import { billingPeriod, plans } from "@/constants";

import type { Plans } from "@/types";
import { pricingSearchParam } from "@/filters";

interface PriceProps {
  isFree?: boolean;
  isAnnual: boolean;
  price: number;
  plan: Plans;
  toggle: (checked: boolean) => void;
  switchAlign?: "start" | "center";
}

const PriceItem = ({
  isFree,
  isAnnual,
  price,
  plan,
  toggle,
  switchAlign = "center",
}: PriceProps) => {
  const annualMonthlyPrice = isFree ? 0 : Math.round(price * 0.8);

  const parsedPrice = isAnnual && !isFree ? annualMonthlyPrice : price;

  return (
    <div className="flex flex-col gap-4">
      {isFree ? null : (
        <div className={`flex items-center justify-${switchAlign} gap-2`}>
          <Label
            htmlFor={`pricing-toggle-${plan}`}
            className={`text-sm font-medium ${!isAnnual ? "text-primary" : "text-muted-foreground"}`}
          >
            Mensal
          </Label>
          <Switch
            id={`pricing-toggle-${plan}`}
            checked={isAnnual}
            onCheckedChange={toggle}
          />
          <Label
            htmlFor={`pricing-toggle-${plan}`}
            className={`text-sm font-medium ${isAnnual ? "text-primary" : "text-muted-foreground"}`}
          >
            Anual{" "}
            <span className="text-green-600 text-xs">(2 meses grátis)</span>
          </Label>
        </div>
      )}
      <div className="flex flex-col gap-2">
        {isFree ? (
          <p className="text-2xl font-bold pt-2">Grátis</p>
        ) : (
          <div>
            <p className="text-3xl font-bold">
              R${parsedPrice}{" "}
              <span className="text-lg font-normal text-muted-foreground">
                / mês
              </span>
            </p>
            {isAnnual ? (
              <>
                <p className="text-sm text-green-600 font-medium mt-1">
                  Economize 20% com plano anual
                </p>
              </>
            ) : null}
          </div>
        )}
        {isFree ? null : (
          <div className="flex flex-col gap-1">
            <p className="text-muted-foreground text-xs">
              {isAnnual
                ? `Cobrança anual de R${annualMonthlyPrice * 12}.`
                : null}{" "}
              Teste grátis de 5 dias, cancele a qualquer momento.
            </p>
            <p className="text-muted-foreground text-xs">
              Reembolso total em até 30 dias.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

interface Feature {
  title: string;
  description: string;
}

interface PricingCardProps {
  plan: Plans;
  title: string;
  price: number;
  description: string;
  features: Feature[];
  excludedFeatures?: Feature[];
  popular?: boolean;
  buttonProps?: {
    label?: string;
    hidden?: boolean;
  };
}

const PriceCard = ({
  plan,
  title,
  price,
  description,
  features,
  excludedFeatures = [],
  popular,
  buttonProps,
}: PricingCardProps) => {
  const [isAnnual, setIsAnnual] = useState(false);

  const isFree = price === 0;

  const toggle = (checked: boolean) => {
    setIsAnnual(checked);
  };

  return (
    <Card
      className={`relative overflow-hidden ${
        popular ? "border-primary border-2" : ""
      }`}
    >
      {popular ? (
        <div className="absolute top-0 right-0 bg-primary text-white px-4 py-1 rounded-bl-lg text-sm font-semibold">
          Mais popular
        </div>
      ) : null}
      <CardHeader>
        <CardTitle className="text-2xl font-bold flex flex-row items-center gap-3">
          <div>
            <img
              loading="eager"
              src={plans[plan].icon}
              alt={`Plano ${plan}`}
              height={16}
              width={16}
            />
          </div>
          {title}
        </CardTitle>
        <div className="flex flex-col gap-6">
          <p className="text-muted-foreground">{description}</p>
          <PriceItem
            isFree={isFree}
            isAnnual={isAnnual}
            price={price}
            plan={plan}
            toggle={toggle}
          />
        </div>
      </CardHeader>
      <CardContent className="p-6 pt-1">
        {buttonProps?.hidden ? null : (
          <Button
            variant={popular ? "default" : "secondary"}
            className="w-full mb-10"
            asChild
          >
            {isFree ? (
              <Link href="/sign-up/">Criar conta</Link>
            ) : (
              <Link
                href={`/checkout?${pricingSearchParam.plano}=${plan}&${pricingSearchParam.periodo}=${isAnnual ? billingPeriod.anual : billingPeriod.mensal}`}
              >
                {buttonProps?.label || "Testar grátis por 5 dias"}
              </Link>
            )}
          </Button>
        )}
        <div className="flex flex-col gap-2">
          <p className="font-semibold mb-2">O que está incluído:</p>
          <PriceList features={features} excludedFeatures={excludedFeatures} />
        </div>
      </CardContent>
    </Card>
  );
};

PriceCard.Card = PriceCard;
PriceCard.Price = PriceItem;

export { PriceCard };
